/**
 * TIMELINE (P4, PLAN-TO-EXECUTE.md §5) — the plan laid out by week: a thin MILESTONE band on top (one row +
 * colour per milestone, so overlapping milestones read side by side), one lane per lead agent below — a
 * lane grows sub-rows so PARALLEL tasks overlap visibly (the scheduler is dependency-only by default;
 * "Gate milestones" is the sign-off view), bars = tasks filled with their milestone's colour and a
 * top stripe for the critical path (red) vs the rest (blue). The agent column + the header stay put while
 * scrolling. A click opens the task .md in the dock (same as Graph/Plan); drag a bar to move it, drag its
 * right edge to resize — the server refuses a start before the task's producers end (409) and the user may
 * "Save anyway", never saved silently. The project START date (shared, stored per project) dates the weeks.
 * Pure view over ``timeline`` (derived on read by the backend scheduler).
 */
import { usePlanWords } from '../../utils/planWords.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';

const DAY_PX = 26;
const ROW_PX = 26;                                     // one sub-row inside an agent lane
const LABEL_PX = 150;
const BAND_PX = 12;                                    // one milestone row in the band
const CLICK_PX = 3;                                    // less movement than this = a click, not a drag

function snap(v) { return Math.max(0, Math.round(v * 2) / 2); }

/** Milestone colour by index — golden-angle hues, so ANY number of milestones get distinct colours. */
export function milestoneColor(i) {
  const h = Math.round(((Number(i) || 0) * 137.508) % 360);
  return { fill: `hsl(${h}, 62%, 52%)`, edge: `hsl(${h}, 62%, 36%)`, soft: `hsl(${h}, 70%, 92%)` };
}

function isoDay(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDay(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Working day ``n`` (0 = the start) → calendar date: weekends skipped; a weekend start rolls to Monday. */
export function workingDayDate(startIso, n) {
  const d = parseDay(startIso) || new Date();
  const step = () => { while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1); };
  step();
  for (let k = 0; k < Math.floor(Math.max(0, n)); k += 1) { d.setDate(d.getDate() + 1); step(); }
  return d;
}

/** Pack a lane's bars into sub-rows: each bar takes the lowest row free at its start → ``{rowOf, rows}``. */
export function packRows(items) {
  const ends = [];
  const rowOf = {};
  [...(items || [])].sort((a, b) => a.start - b.start || b.days - a.days || String(a.taskId).localeCompare(String(b.taskId)))
    .forEach((i) => {
      let r = ends.findIndex((e) => e <= i.start + 1e-9);
      if (r < 0) { r = ends.length; ends.push(0); }
      ends[r] = i.start + i.days;
      rowOf[i.taskId] = r;
    });
  return { rowOf, rows: Math.max(1, ends.length) };
}

/** Milestone spans (first start → last end) derived from the bars — mirrors ``timeline.milestones``. */
export function spansFromItems(items) {
  const by = new Map();
  (items || []).forEach((i) => {
    const k = i.milestoneIndex ?? 0;
    const sp = by.get(k) || { milestone: i.milestone || `M${k}`, index: k, start: i.start, end: i.end, tasks: 0 };
    sp.start = Math.min(sp.start, i.start); sp.end = Math.max(sp.end, i.end); sp.tasks += 1;
    by.set(k, sp);
  });
  return [...by.values()].sort((a, b) => a.index - b.index);
}

function fmt(d) { return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }

function Bar({ item, row = 0, color, startIso, onCommit, onOpen }) {
  const [drag, setDrag] = useState(null);           // {mode, start, days}
  const live = drag ? { start: drag.start, days: drag.days } : { start: item.start, days: item.days };
  const begin = useCallback((mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    setDrag({ mode, start: item.start, days: item.days });
    const move = (ev) => {
      const dd = (ev.clientX - x0) / DAY_PX;
      setDrag((d) => d && (mode === 'move'
        ? { ...d, start: snap(item.start + dd) }
        : { ...d, days: Math.max(0.5, snap(item.days + dd)) }));
    };
    const up = (ev) => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const dx = ev.clientX - x0;
      const dd = dx / DAY_PX;
      setDrag(null);
      if (mode === 'move' && Math.abs(dx) < CLICK_PX) {  // a CLICK → the task .md in the dock
        if (onOpen) onOpen(item.taskId, item.title);
        return;
      }
      if (mode === 'move') {
        const ns = snap(item.start + dd);
        if (ns !== item.start) onCommit(item.taskId, { startDay: ns });
      } else {
        const nd = Math.max(0.5, snap(item.days + dd));
        if (nd !== item.days) onCommit(item.taskId, { days: nd });
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [item, onCommit, onOpen]);
  // Colours INLINE: a global rule on role=button elements overrode the Tailwind bg (bars rendered slate).
  // HYBRID: fill = the milestone; the top stripe = critical (red) or not (blue).
  const stripe = item.critical ? '#e11d48' : '#2563eb';
  const widthPx = Math.max(8, live.days * DAY_PX - 2);
  const from = fmt(workingDayDate(startIso, live.start));
  const to = fmt(workingDayDate(startIso, Math.max(live.start, live.start + live.days - 0.5)));
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`tl-bar-${item.taskId}`}
      data-critical={item.critical ? 'true' : 'false'}
      data-milestone={item.milestoneIndex ?? ''}
      onMouseDown={begin('move')}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpen) { e.preventDefault(); onOpen(item.taskId, item.title); } }}
      title={`${item.taskId} — ${item.title}\n${item.milestone ? `${item.milestone} · ` : ''}${from} – ${to} · ${live.days}d`
        + `${item.dependsOn && item.dependsOn.length ? `\nafter ${item.dependsOn.join(', ')}` : ''}`
        + `${item.scaled ? `\n${item.baseDays}d → ${live.days}d (fitted to the target)` : ''}`
        + `${item.pinned ? '\nresized by hand — fixed, never fitted' : ''}`
        + `${item.critical ? '\non the critical path' : ''}${item.overridden ? '\nmanually scheduled' : ''}`
        + `${item.readiness === 'needs-attention' ? '\nneeds attention' : ''}`
        + `${(item.waitingOn || []).length ? `\n⏸ waiting on you: ${item.waitingOn.slice(0, 3).join('; ')}` : ''}\nclick to open · drag to move`}
      data-waiting={(item.waitingOn || []).length ? 'true' : 'false'}
      className={`absolute flex h-[22px] cursor-pointer items-center overflow-hidden rounded border px-1 text-[10px] text-white ${drag ? 'opacity-80' : ''}`}
      style={{ top: row * ROW_PX + 2, left: live.start * DAY_PX, width: widthPx, background: color.fill, borderColor: color.edge,
        boxShadow: `inset 0 3px 0 0 ${stripe}` }}
    >
      {/* The id only when it FITS — a 1-day bar read "M…" everywhere; the tooltip always carries it. */}
      {widthPx >= 7 * String(item.taskId).length + 8
        ? <span className="truncate pt-0.5">{item.taskId}{item.overridden ? ' ✎' : ''}</span>
        : (item.overridden ? <span>✎</span> : null)}
      {(item.waitingOn || []).length ? (
        <span data-testid={`tl-waiting-${item.taskId}`} className="absolute left-0.5 top-0.5 text-[9px] leading-none">⏸</span>
      ) : null}
      {item.readiness === 'needs-attention' ? (
        <span data-testid={`tl-attention-${item.taskId}`} className="absolute right-2 top-1 h-1.5 w-1.5 rounded-full"
              style={{ background: '#fbbf24', boxShadow: '0 0 0 1px #92400e' }} />
      ) : null}
      <span
        role="separator"
        aria-label="resize"
        data-testid={`tl-resize-${item.taskId}`}
        onMouseDown={begin('resize')}
        className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-white/30"
      />
    </div>
  );
}

/** Milestone target editor (PortalMenu — never an inline absolute popover): days, Save, Clear. */
function MilestoneTargetMenu({ anchorRef, span, onClose, onSave }) {
  const [val, setVal] = useState(span && span.target ? String(span.target) : '');
  useEffect(() => { setVal(span && span.target ? String(span.target) : ''); }, [span]);
  if (!span) return null;
  const actual = Math.round((span.end - span.start) * 100) / 100;
  return (
    <PortalMenu anchorRef={anchorRef} open={!!span} onClose={onClose} align="bottom-left" menuRole="dialog"
                className="rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
      <div className="w-56 space-y-2 text-[11px] text-slate-700" data-testid="tl-ms-edit">
        <div className="font-semibold">{span.milestone}</div>
        <div className="text-slate-500">now {actual} working days{span.target ? ` · target ${span.target}d` : ''}</div>
        <label className="flex items-center gap-1">
          <span>Fit to</span>
          <input type="number" min="0.5" step="0.5" data-testid="tl-ms-days" value={val}
                 onChange={(e) => setVal(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && Number(val) > 0) onSave(Number(val)); }}
                 className="w-16 rounded border border-slate-300 px-1 py-0.5" />
          <span>days</span>
        </label>
        <div className="flex gap-1">
          <button type="button" data-testid="tl-ms-save" disabled={!(Number(val) > 0)} onClick={() => onSave(Number(val))}
                  className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            Save
          </button>
          {span.target ? (
            <button type="button" data-testid="tl-ms-clear" onClick={() => onSave(null)}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-slate-600 hover:bg-slate-100">
              Clear
            </button>
          ) : null}
        </div>
      </div>
    </PortalMenu>
  );
}

export default function TimelineView({ timeline, onSchedule, onSaveSettings = null, onOpenTask = null,
  gateMilestones = false, onToggleGate = null }) {
  const w = usePlanWords();
  const [pending, setPending] = useState(null);      // {taskId, patch, message}
  const [busy, setBusy] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [msEdit, setMsEdit] = useState(null);         // the milestone span whose target is being edited
  const msAnchor = useRef(null);
  const settingsIn = (timeline && timeline.settings) || {};
  const [weeksVal, setWeeksVal] = useState('');
  useEffect(() => {
    const dpw0 = (timeline && timeline.daysPerWeek) || 5;
    setWeeksVal(settingsIn.targetDays ? String(Math.round((settingsIn.targetDays / dpw0) * 10) / 10) : '');
  }, [settingsIn.targetDays, timeline]);
  const scrollRef = useRef(null);
  const commit = useCallback(async (taskId, patch, force = false) => {
    if (!onSchedule) return;
    setBusy(true);
    try {
      const res = await onSchedule(taskId, { ...patch, ...(force ? { force: true } : {}) });
      if (res && res.conflict) setPending({ taskId, patch, message: res.message || 'conflicts with its producers' });
      else setPending(null);
    } finally {
      setBusy(false);
    }
  }, [onSchedule]);
  const save = useCallback(async (patch) => {
    if (!onSaveSettings) return;
    setStartErr('');
    setBusy(true);
    try { await onSaveSettings(patch); } catch (err) { setStartErr((err && err.message) || 'could not save'); }
    finally { setBusy(false); }
  }, [onSaveSettings]);
  const changeStart = useCallback((e) => { if (e.target.value) save({ startDate: e.target.value }); }, [save]);

  const tl = timeline || {};
  const items = tl.items || [];
  if (!items.length) {
    return <div className="p-3 text-xs text-slate-500" data-testid="tl-empty">No schedulable tasks yet.</div>;
  }
  const dpw = tl.daysPerWeek || 5;
  const weeks = Math.max(1, tl.weeks || Math.ceil((tl.totalDays || 1) / dpw));
  const lanes = tl.lanes || [...new Set(items.map((i) => i.lane))];
  const width = weeks * dpw * DAY_PX;
  const startIso = (tl.start && tl.start.startDate) || isoDay(new Date());   // unset → today
  const spans = tl.milestones || spansFromItems(items);
  const lockedIds = new Set(tl.locked || []);             // milestone ids locked for execution (P5a)   // an older backend without ``milestones``
  const colorOf = (i) => milestoneColor(i.milestoneIndex ?? 0);
  const stickyCell = { width: LABEL_PX, position: 'sticky', left: 0, zIndex: 3 };
  const targets = tl.targets || {};
  const unfit = [targets.project, ...(targets.milestones || [])].filter((r) => r && r.fit === false);
  const commitWeeks = () => {
    const w = Number(weeksVal);
    const cur = settingsIn.targetDays || null;
    const next = weeksVal === '' ? null : (w > 0 ? Math.round(w * dpw * 100) / 100 : undefined);
    if (next === undefined || next === cur) return;
    save({ targetDays: next });
  };
  return (
    <div className="space-y-2" data-testid="tl-view">
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
        <label className="flex items-center gap-1">
          <span>Start</span>
          <input type="date" data-testid="tl-start" value={startIso} onChange={changeStart} disabled={!onSaveSettings}
                 className="rounded border border-slate-300 px-1 py-0.5 text-[11px]" />
          {tl.start && tl.start.startDate ? null : <span className="text-slate-400" data-testid="tl-start-default">(today)</span>}
        </label>
        <label className="flex items-center gap-1"
               title="Fit the whole plan into this many weeks: task durations scale (dependencies and overlap unchanged; resized tasks stay fixed). Empty = the estimates.">
          <span>Weeks</span>
          <input type="number" min="0.5" step="1" data-testid="tl-weeks" value={weeksVal} placeholder={String(weeks)}
                 disabled={!onSaveSettings} onChange={(e) => setWeeksVal(e.target.value)} onBlur={commitWeeks}
                 onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                 className="w-14 rounded border border-slate-300 px-1 py-0.5 text-[11px]" />
          {settingsIn.targetDays ? (
            <button type="button" data-testid="tl-weeks-clear" onClick={() => { setWeeksVal(''); save({ targetDays: null }); }}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-slate-500 hover:bg-slate-100">
              Clear
            </button>
          ) : <span className="text-slate-400">(estimates)</span>}
        </label>
        {onToggleGate ? (
          <label className="flex items-center gap-1" title="Off: tasks wait only on the work they need, so independent work runs in parallel. On: each milestone starts after the previous one ends (sign-off view).">
            <input type="checkbox" data-testid="tl-gate" checked={!!gateMilestones}
                   onChange={(e) => onToggleGate(e.target.checked)} />
            <span>Gate milestones</span>
          </label>
        ) : null}
        <span data-testid="tl-summary"><b>{tl.totalDays}</b> working days · <b>{weeks}</b> week{weeks === 1 ? '' : 's'} · {items.length} tasks · {lanes.length} lanes</span>
        <span className="flex items-center gap-1"><span className="h-1 w-3 rounded" style={{ background: '#e11d48' }} />critical path</span>
        <span className="flex items-center gap-1"><span className="h-1 w-3 rounded" style={{ background: '#2563eb' }} />other</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: '#fbbf24' }} />needs attention</span>
        <span className="text-slate-400">click to open · drag to move · drag the right edge to resize</span>
        {busy ? <span className="text-slate-400">saving…</span> : null}
        {startErr ? <span className="text-rose-600" data-testid="tl-start-error">{startErr}</span> : null}
      </div>
      {unfit.length ? (
        <div className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="tl-fit-warning">
          Target not met — {unfit.map((r) => `${r.milestone ? `${r.milestone}: ` : ''}${r.reason}`).join(' · ')}
        </div>
      ) : null}
      {(tl.conflicts || []).length ? (
        <div className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="tl-conflicts">
          {(tl.conflicts || []).length} manually scheduled task(s) start before their producers end:{' '}
          {(tl.conflicts || []).map((c) => `${c.taskId} (${c.reason})`).join(' · ')}
        </div>
      ) : null}
      {pending ? (
        <div className="flex items-center gap-2 rounded bg-rose-50 px-2 py-1 text-[11px] text-rose-800" data-testid="tl-refused">
          <span>Not saved — {pending.message}.</span>
          <button type="button" data-testid="tl-force"
            onClick={() => commit(pending.taskId, pending.patch, true)}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-600 text-white hover:bg-rose-700">
            Save anyway
          </button>
          <button type="button" onClick={() => setPending(null)}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-rose-700 hover:bg-rose-100">
            Cancel
          </button>
        </div>
      ) : null}
      <div ref={scrollRef} className="max-h-[70vh] overflow-auto rounded border border-slate-200" data-testid="tl-scroll">
        <div style={{ width: LABEL_PX + width }}>
          {/* HEADER (sticky top): week columns dated from the project start + the thin milestone band */}
          <div style={{ position: 'sticky', top: 0, zIndex: 4 }} className="bg-slate-50">
            <div className="flex border-b border-slate-200 text-[10px] font-medium text-slate-500">
              <div style={stickyCell} className="shrink-0 bg-slate-50 px-2 py-1">Lane</div>
              {Array.from({ length: weeks }, (_, w) => (
                <div key={w} style={{ width: dpw * DAY_PX }} className="shrink-0 border-l border-slate-200 px-1 py-1"
                     data-testid={`tl-week-${w + 1}`}>
                  Week {w + 1} · <span data-testid={`tl-week-date-${w + 1}`}>{fmt(workingDayDate(startIso, w * dpw))}</span>
                </div>
              ))}
            </div>
            {spans.length ? (
              <div className="flex border-b border-slate-200" data-testid="tl-milestones">
                <div style={stickyCell} className="shrink-0 bg-slate-50 px-2 text-[10px] leading-[14px] text-slate-500">{w.groups}</div>
                <div className="relative shrink-0" style={{ width, height: BAND_PX * spans.length + 2 }}>
                  {spans.map((m, row) => {
                    const c = milestoneColor(m.index);
                    const isLocked = lockedIds.has(`M${m.index}`);
                    return (
                      <div key={m.index} data-testid={`tl-ms-${m.index}`} role="button" tabIndex={0}
                           onClick={(e) => { if (!onSaveSettings) return; msAnchor.current = e.currentTarget; setMsEdit(m); }}
                           title={`${m.milestone} · ${fmt(workingDayDate(startIso, m.start))} – ${fmt(workingDayDate(startIso, Math.max(m.start, m.end - 0.5)))} · ${m.tasks} task(s)`
                             + `${m.target ? ` · fitted to ${m.target}d` : ''}${isLocked ? '\nlocked — its tasks can run; this schedule is its baseline' : ''}`
                             + `${onSaveSettings ? '\nclick to set its days' : ''}`}
                           data-locked={isLocked ? 'true' : 'false'}
                           className={`absolute h-[10px] overflow-hidden whitespace-nowrap rounded-sm px-1 text-[8px] font-semibold leading-[10px] text-white ${onSaveSettings ? 'cursor-pointer' : ''}`}
                           style={{ top: row * BAND_PX + 1, left: m.start * DAY_PX, width: Math.max(4, (m.end - m.start) * DAY_PX - 1), background: c.fill,
                             ...(m.target ? { outline: '1px dashed #0f172a', outlineOffset: 0 } : {}) }}>
                        {isLocked ? '🔒 ' : ''}{m.milestone}{m.target ? ` · ${m.target}d` : ''}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          {lanes.map((lane) => {
            const laneItems = items.filter((i) => i.lane === lane);
            const { rowOf, rows } = packRows(laneItems);
            return (
            <div key={lane} className="flex border-b border-slate-100" data-testid="tl-lane" data-rows={rows}>
              <div style={stickyCell} className="shrink-0 truncate bg-white px-2 pt-1 text-[11px] leading-[20px] text-slate-700"
                   title={lane} data-testid="tl-lane-label">
                {lane}
              </div>
              <div className="relative shrink-0" style={{ width, height: rows * ROW_PX + 4 }}>
                {Array.from({ length: weeks }, (_, w) => (
                  <div key={w} className="absolute top-0 h-full border-l border-slate-100" style={{ left: w * dpw * DAY_PX }} />
                ))}
                {laneItems.map((i) => (
                  <Bar key={i.taskId} item={i} row={rowOf[i.taskId]} color={colorOf(i)} startIso={startIso}
                       onCommit={commit} onOpen={onOpenTask} />
                ))}
              </div>
            </div>
            );
          })}
        </div>
      </div>
      <MilestoneTargetMenu anchorRef={msAnchor} span={msEdit} onClose={() => setMsEdit(null)}
        onSave={(d) => { const m = msEdit; setMsEdit(null); if (m) save({ milestone: m.milestone, milestoneDays: d }); }} />
    </div>
  );
}
